/** Tri-state for API query params displayInApp / has_live_data (omit = all / legacy default). */
export type EditorTriState = 'all' | 'true' | 'false';

/**
 * Map-only: optional second layer of lots for comparison (muted style).
 * Not sent to the API as a query param — see `appendEditorFiltersToSearchParams`.
 */
export type MapContextMode = 'none' | 'paid' | 'non_matching';

export interface ParkingLotsEditorFiltersState {
    /** Empty = no filter (all types). Values: free | paid | limited_time */
    parkingTypes: string[];
    owner: string;
    displayInApp: EditorTriState;
    liveData: EditorTriState;
    /** Secondary map layer preset (editor map only). */
    mapContextMode: MapContextMode;
}

export const DEFAULT_PARKING_LOTS_EDITOR_FILTERS: ParkingLotsEditorFiltersState = {
    parkingTypes: [],
    owner: '',
    displayInApp: 'all',
    liveData: 'all',
    mapContextMode: 'none',
};

const ALLOWED_PARKING_TYPES = new Set(['free', 'paid', 'limited_time']);

export function appendEditorFiltersToSearchParams(
    params: URLSearchParams,
    f: ParkingLotsEditorFiltersState
): void {
    const types = f.parkingTypes.filter((t) => ALLOWED_PARKING_TYPES.has(t));
    if (types.length > 0) {
        params.set('parking_type', types.join(','));
    }
    const owner = f.owner.trim();
    if (owner) params.set('owner', owner);
    // Always send displayInApp (including 'all') so the backend doesn't fall through
    // to its public default of hiding displayInApp=false lots on admin map fetches.
    params.set('displayInApp', f.displayInApp);
    if (f.liveData !== 'all') params.set('has_live_data', f.liveData);
}

/**
 * Stable string for SWR cache keys and primary/list API identity.
 * Excludes `mapContextMode` so toggling the context layer does not refetch list or primary map data.
 */
export function parkingLotsEditorFiltersKey(f: ParkingLotsEditorFiltersState): string {
    const types = f.parkingTypes.filter((t) => ALLOWED_PARKING_TYPES.has(t)).slice().sort().join(',');
    return [types, f.owner.trim(), f.displayInApp, f.liveData].join('|');
}

/** SWR key segment for the secondary (context) map fetch. */
export function parkingLotsMapContextLayerKey(mode: MapContextMode, secondaryFiltersKey: string): string {
    return `${mode}|${secondaryFiltersKey}`;
}

/**
 * Filter state used for the context-layer GET (not merged with primary filters).
 * Returns null when no secondary layer should be loaded.
 */
export function mapContextSecondaryFilters(mode: MapContextMode): ParkingLotsEditorFiltersState | null {
    if (mode === 'none') return null;
    if (mode === 'paid') {
        return {
            ...DEFAULT_PARKING_LOTS_EDITOR_FILTERS,
            parkingTypes: ['paid'],
        };
    }
    if (mode === 'non_matching') {
        return { ...DEFAULT_PARKING_LOTS_EDITOR_FILTERS };
    }
    return null;
}

export function countActiveEditorFilters(f: ParkingLotsEditorFiltersState): number {
    let n = 0;
    if (f.parkingTypes.some((t) => ALLOWED_PARKING_TYPES.has(t))) n++;
    if (f.owner.trim()) n++;
    if (f.displayInApp !== 'all') n++;
    if (f.liveData !== 'all') n++;
    return n;
}

export function toggleParkingType(
    f: ParkingLotsEditorFiltersState,
    type: string
): ParkingLotsEditorFiltersState {
    if (!ALLOWED_PARKING_TYPES.has(type)) return f;
    const set = new Set(f.parkingTypes);
    if (set.has(type)) set.delete(type);
    else set.add(type);
    return { ...f, parkingTypes: Array.from(set) };
}
